/**
 * Backoffice Login Page Component
 * @implements FS-001.2.1 - OAuth Authentication (GitHub + Google)
 * @implements FS-001.4.1 - Login Page Component
 *
 * Login page for backoffice administrators with OAuth authentication.
 * Shows GitHub and Google OAuth buttons as primary login methods.
 * Handles OAuth error callbacks from URL query params.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Collapse,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { GitHubOAuthButton, GoogleOAuthButton } from '@/components/auth';

/**
 * Map OAuth error codes to user-friendly messages
 * @implements FS-001.4.1 - Error handling
 */
const getErrorMessage = (code: string, provider?: string | null): string => {
  if (code === 'provider_mismatch' && provider) {
    const providerName = provider === 'github' ? 'GitHub' : 'Google';
    return `This email is already registered with ${providerName}. Please sign in using ${providerName} instead.`;
  }

  const messages: Record<string, string> = {
    provider_mismatch: 'Please sign in with your registered OAuth provider.',
    user_not_found:
      'No account found. Please contact an administrator for access.',
    account_suspended: 'Your account has been suspended. Contact support.',
    oauth_cancelled: 'Authentication was cancelled.',
    oauth_failed: 'OAuth authentication failed. Please try again.',
    provider_unavailable: 'OAuth provider is currently unavailable.',
    oauth_timeout: 'OAuth request timed out. Please try again.',
  };
  return messages[code] || 'An error occurred. Please try again.';
};

export const LoginPage = () => {
  const [error, setError] = useState<string | null>(null);

  // URL params for OAuth callback errors
  const [searchParams] = useSearchParams();
  const authError = searchParams.get('error');
  const authProvider = searchParams.get('provider');

  /**
   * Handle OAuth callback errors from URL query params
   * @implements FS-001.4.1 - OAuth error handling
   */
  useEffect(() => {
    if (authError) {
      setError(getErrorMessage(authError, authProvider));
    }
  }, [authError, authProvider]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Backoffice Login
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Sign in to access the Jared Backoffice
          </Typography>

          {/* Error Alert */}
          <Collapse in={!!error}>
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          </Collapse>

          {/* OAuth Buttons
           * @implements FS-001.2.1 - GitHub and Google OAuth Authentication
           */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <GitHubOAuthButton />
            <GoogleOAuthButton />
          </Box>

          {/* Provider lock-in helper text */}
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 3 }}
          >
            Please sign in with the same provider you used to register.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};
